import { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, Ref, useCallback, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { FormattedInput } from '@flipster/react/design-utils/formatted-input';
import XCircleIcon from '@flipster/react/icon/generated/XCircleIcon';
import { useMergeRef } from '@flipster/react/misc/ref/use-merge-ref';
import { useControllableState } from '@flipster/react/misc/state/use-controllable-state';
import { css, cx } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import Num, { inputFormatter, numberFormatter } from '@flipster/utils/num';

export interface ClearableNumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'value' | 'onChange'> {
  ref?: Ref<HTMLInputElement>;
  className?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onClearClick?: () => void;
  precision: number;
  max?: string;
  maxLength?: number;
  hideClearButton?: boolean;
  determineShowClearButton?: (innerValue?: string) => boolean;
}

export interface ClearableNumberInputWithFrameProps extends ClearableNumberInputProps {
  isError?: boolean;
  prefix?: ReactNode;
  suffix?: string | ReactNode;
  currency?: string;
  customStyle?: boolean;
}

export const ClearableNumberInputWithFrame = ({
  className,
  isError: _isError, // <FormItem>에서 강제 주입되는 prop. 사용하지 않음
  prefix,
  suffix,
  currency,
  customStyle,
  ...props
}: ClearableNumberInputWithFrameProps) => {
  return (
    <div className={cx(className, hstack(), !customStyle && frameStyle)}>
      {prefix}
      <ClearableNumberInput {...props} />
      {suffix}
      {currency && <span translate='no'>{currency}</span>}
    </div>
  );
};

const frameStyle = css({
  h: 48,
  px: 12,
  textStyle: 'Text/L/Monospace/Regular',
  color: 'content.default.level-5',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default.level-2',
  borderRadius: 4,
  transition: 'border-color 200ms',
  _hover: {
    borderColor: 'border.default.level-4',
  },
  _focusWithin: {
    borderColor: 'border.default.level-4',
  },
});

export interface ControlClearableNumberInputWithFrameProps extends Omit<ClearableNumberInputWithFrameProps, 'value'> {
  name: string;
}
export const ControlClearableNumberInputWithFrame = ({
  name,
  onChange: onChangeCallback,
  ...props
}: ControlClearableNumberInputWithFrameProps) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange, onBlur, ref },
  } = useController({ name, control });
  return (
    <ClearableNumberInputWithFrame
      {...props}
      ref={ref}
      name={name}
      value={value}
      onBlur={onBlur}
      onChange={(val) => {
        onChange(val);
        onChangeCallback?.(val);
      }}
    />
  );
};

// field-sizing 속성을 이용하면 코드를 훨씬 단순하게 만들 수 있지만
// 브라우저 지원범위가 매우 협소해서 안보이는 div로 위치를 잡아주는 방식으로 구현함.
// - https://developer.chrome.com/docs/css-ui/css-field-sizing
// - https://caniuse.com/?search=field-sizing
export const ClearableNumberInput = ({
  ref,
  className,
  precision,
  max,
  onChange,
  onClearClick: onClearClickCallback,
  value,
  defaultValue,
  hideClearButton,
  determineShowClearButton = Boolean,
  ...props
}: ClearableNumberInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = useMergeRef(inputRef, ref);
  const [innerValue, setInnerValue] = useControllableState({
    value,
    defaultValue,
    onChange(nextValue) {
      if (checkOverMax(nextValue, max)) {
        onChange?.(numberFormatter(max, { withComma: false, precision }));
        inputRef.current?.setSelectionRange(-1, -1);
        return;
      }
      onChange?.(nextValue);
    },
  });
  const onClearClick = useCallback(() => {
    // segment 로깅시 clear 이전의 value를 알아야 하기 때문에
    // onClearClickCallback 호출이 setInnerValue 호출보다 먼저 위치해야 함
    onClearClickCallback?.();
    setInnerValue('');
  }, [setInnerValue, onClearClickCallback]);
  const formatter = useCallback((val: string) => inputFormatter(val, { withComma: true, precision }), [precision]);
  const deformatter = useCallback((val: string) => val.replace(/[^\d.]/g, ''), []);
  const formattedValue = formatter(innerValue || '');
  const showClearButton = !hideClearButton && determineShowClearButton(innerValue);

  return (
    <div
      translate='no'
      className={cx(
        className,
        hstack({
          h: 'inherit',
          pos: 'relative',
          flexBasis: 0,
          flexGrow: 1,
          '&:focus-within .clear': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }),
      )}
    >
      <FormattedInput
        ref={mergedRef}
        className={css({
          font: 'inherit',
          pos: 'absolute',
          p: 'inherit',
          pr: 0,
          minW: 0,
          w: '100%',
          h: '100%',
          color: 'inherit',
          zIndex: 1,
        })}
        value={innerValue}
        onChange={setInnerValue}
        formatter={formatter}
        deformatter={deformatter}
        maxLength={12}
        inputMode='numeric'
        autoCapitalize='off'
        autoComplete='off'
        autoCorrect='off'
        spellCheck='false'
        data-lpignore='true'
        {...props}
      />
      <div
        className={css({
          pos: 'relative',
          font: 'inherit',
          letterSpacing: 'normal',
        })}
      >
        <p className={css({ opacity: 0, pointerEvents: 'none', userSelect: 'none' })}>
          <wbr />
          {formattedValue}
        </p>
        {showClearButton && (
          <ClearButton
            className={cx(
              'clear',
              css({
                pos: 'absolute',
                top: '50%',
                left: '100%',
                transform: 'translateY(-50%)',
                opacity: 0,
                transition: 'opacity 0.15s',
                pointerEvents: 'none',
                zIndex: 2,
              }),
            )}
            onClick={onClearClick}
          />
        )}
      </div>
    </div>
  );
};

export function showClearButtonWhenValueIsNotZero(innerValue?: string): boolean {
  if (!innerValue) return false;
  return !Num(innerValue).isZero();
}

function checkOverMax(val?: string, max?: string): boolean {
  return val != null && max != null && Num(val).isGreaterThan(max);
}

export interface ClearButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {}
export const ClearButton = ({ className, ...props }: ClearButtonProps) => {
  return (
    <button
      {...props}
      className={cx(className, css({ w: 32, h: 32, lineHeight: 0 }))}
      onMouseDown={(e) => {
        // https://prex.atlassian.net/browse/QA-14252
        // Safari 브라우저에서는 버튼 클릭 시 input의 focus가 사라져 버튼이 제대로 작동하지 않음
        // preventDefault()를 호출하면 focus를 유지할 수 있음
        e.preventDefault();
      }}
    >
      <XCircleIcon size={20} className={css({ color: 'content.default.level-1' })} />
    </button>
  );
};
